import React, { useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { usePushStore, AlarmItem } from "@shared/libs/pushStore";

type TabKey = "ALL" | "UNREAD" | "READ";

const TABS: { key: TabKey; label: string }[] = [
  { key: "ALL", label: "전체" },
  { key: "UNREAD", label: "미확인" },
  { key: "READ", label: "확인" },
];

type Props = {
  onClose: () => void;
  /** 하단 "메시지센터" 버튼 클릭 → SN_ETC_ALR_REP_001 탭 열기 */
  onOpenMessageCenter?: () => void;
};

/**
 * FcmAlarmMessageBox - 헤더 알림종을 눌렀을 때 아래로 펼쳐지는 알림 메시지 팝업.
 * - 목록은 pushStore.items (백엔드 /alarm/list = FcmAlarmMapper.getRecentFcmAlarm, 최근 30일 100건)
 * - 상단: 제목 / 모두읽음 / 닫기,  탭: 전체 · 미확인 · 확인,  하단: 메시지센터 버튼
 * 위치/바깥클릭 닫힘은 부모(FcmNotificationBell)가 담당한다.
 */
export default function FcmAlarmMessageBox({
  onClose,
  onOpenMessageCenter,
}: Props) {
  const items = usePushStore((state) => state.items);
  const unreadCount = usePushStore((state) => state.unreadCount);
  const loading = usePushStore((state) => state.loading);
  const readAll = usePushStore((state) => state.readAll);
  const markRead = usePushStore((state) => state.markRead);
  const hasUnread = unreadCount > 0;
  const [tab, setTab] = useState<TabKey>("ALL");

  const counts = useMemo(
    () => ({
      ALL: items.length,
      UNREAD: unreadCount,
      READ: items.length - unreadCount,
    }),
    [items, unreadCount]
  );

  const visibleItems = useMemo(() => {
    if (tab === "UNREAD") return items.filter((i) => i.yn_read === "N");
    if (tab === "READ") return items.filter((i) => i.yn_read === "Y");
    return items;
  }, [items, tab]);

  return (
    <div
      id="fcmAlarmMessageBox"
      className="fcm-alarm-message-box absolute right-0 top-full mt-2 w-[520px] bg-white border border-gray-300 rounded-xl shadow-lg z-20 flex flex-col overflow-hidden"
      style={{ maxHeight: "70vh" }}
    >
      {/* 헤더: 제목 / 모두읽음 / 닫기 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-baseline gap-2">
          <span className="font-bold text-base text-gray-800">알림</span>
          <span className="text-xs text-gray-400">(최근 30일간의 100건)</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="text-sm text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline"
            onClick={() => readAll()}
            disabled={!hasUnread || loading}
          >
            모두읽음
          </button>
          <button
            type="button"
            className="text-gray-500 hover:text-gray-800 flex items-center"
            aria-label="닫기"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* 탭 */}
      <div className="flex border-b border-gray-200 text-sm">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              className={`flex-1 py-2 text-center border-b-2 -mb-px ${
                active
                  ? "border-blue-600 text-blue-600 font-bold"
                  : "border-transparent text-gray-500 hover:text-gray-800"
              }`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
              <span className="ml-1 text-xs">({counts[t.key]})</span>
            </button>
          );
        })}
      </div>

      {/* 목록 영역 */}
      <div className="flex-1 overflow-y-auto min-h-[200px]">
        {visibleItems.length === 0 ? (
          <FcmAlarmEmpty />
        ) : (
          visibleItems.map((item) => (
            <FcmAlarmMessageRow
              key={item.no_message}
              item={item}
              onClick={() => {
                if (item.yn_read === "N") markRead(item.no_message);
              }}
            />
          ))
        )}
      </div>

      {/* 하단 */}
      <div className="border-t border-gray-200">
        <button
          type="button"
          className="w-full py-3 text-sm font-bold text-gray-700 hover:bg-gray-50"
          onClick={() => {
            onOpenMessageCenter?.();
            onClose();
          }}
        >
          메시지센터
        </button>
      </div>
    </div>
  );
}

function FcmAlarmEmpty() {
  return (
    <div className="h-full min-h-[200px] flex items-center justify-center text-sm text-gray-400">
      알림이 없습니다.
    </div>
  );
}

/** "yyyy-MM-dd HH:mm:ss(.SSS)" → 방금전 / n분전 / n시간전 / MM-DD HH:mm */
export function relativeTimeLabel(tm: string | number | null | undefined): string {
  if (tm === null || tm === undefined || tm === "") return "";
  // /alarm/list 는 SearchMapper 의 날짜 문자열 변환을 거치지 않아 Timestamp 가
  // epoch(ms) 숫자로 올 수 있다. 문자열/숫자 모두 Date 로 정규화한다.
  const date =
    typeof tm === "number"
      ? new Date(tm)
      : new Date(String(tm).replace(" ", "T").substring(0, 19));
  const t = date.getTime();
  if (Number.isNaN(t)) return String(tm);
  const pad = (n: number) => String(n).padStart(2, "0");
  const absLabel = `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  const diffMin = Math.floor((Date.now() - t) / 60000);
  if (diffMin < 1) return "방금전";
  if (diffMin < 60) return `${diffMin}분전`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}시간전`;
  return absLabel; // MM-DD HH:mm
}

/**
 * 알림 한 건의 행.
 *  [●] 메뉴명            관련번호    n분전
 *      메시지 내용
 */
export function FcmAlarmMessageRow(props: {
  item: AlarmItem;
  onClick?: () => void;
}) {
  const { item, onClick } = props;
  const unread = item.yn_read === "N";
  return (
    <div
      className={`flex gap-3 px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
        unread ? "bg-blue-50/40" : ""
      }`}
      onClick={onClick}
    >
      <span className="mt-1.5 flex-shrink-0">
        <span
          className={`block h-2.5 w-2.5 rounded-full ${
            unread ? "bg-blue-500" : "bg-transparent"
          }`}
        />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-bold text-gray-800 truncate">
            {item.nm_menu || "알림"}
          </span>
          {item.no_ref && (
            <span className="ml-auto text-blue-600 font-semibold whitespace-nowrap">
              {item.no_ref}
            </span>
          )}
          <span
            className={`text-xs text-gray-400 whitespace-nowrap ${
              item.no_ref ? "" : "ml-auto"
            }`}
          >
            {relativeTimeLabel(item.tm_reg)}
          </span>
        </div>
        <div className="mt-1 text-sm text-gray-700 break-words whitespace-pre-line">
          {item.dc_message}
        </div>
      </div>
    </div>
  );
}
